import { useContext, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	autoReorderTransactions,
	getTransactionsList,
	reorderTransactionAfterOther,
} from '@dealstack/frontend/app/services/Transactions.service.ts';
import {
	CurrencyType,
	GetTransactionsRes,
	OrderType,
	TransactionListQueryDto,
	TransactionTitle,
} from '@dealstack/shared/lib/generated';
import {
	getTransactionsTableColumns,
	INTEREST_COLUMN_ID,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/transactionsTableColumns.ts';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { getTransferInterestOptions } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/getTransactionInterestText.ts';

interface Props {
	transactionDate: string;
	personId: string;
	entityCurrency?: CurrencyType | null;
	/** CAP-2093: the grid's interest filter, so the reorder view opens on the same rail. */
	initialTitles?: TransactionTitle[] | null;
}

export const useReorderTransactionsController = ({
	transactionDate,
	personId,
	entityCurrency,
	initialTitles,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const queryClient = useQueryClient();
	const { deal } = useContext(DealContext);

	// CAP-1062: with the nominee feature flag off, the legal/beneficial "Interest" column is
	// dropped here too.
	const nomineeStructuresEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_NOMINEE_STRUCTURES
	).value;
	// CAP-2093: same gate as the grid's filter — a deal without the switch has one interest.
	const showInterestFilter = nomineeStructuresEnabled && !!deal?.hasNomineeStructure;
	const interestOptions = useMemo(() => getTransferInterestOptions(tCapManage), [tCapManage]);
	const [titles, setTitles] = useState<TransactionTitle[]>(initialTitles ?? []);
	const onTitlesChange = (values: string[]) =>
		setTitles(
			interestOptions
				.filter((option) => values.includes(option.value))
				.map((option) => option.value)
		);

	const transactionsRequestParams: TransactionListQueryDto = {
		personId,
		fetchDeleted: false,
		filter: {
			dateFrom: transactionDate,
			dateTo: transactionDate,
			...(showInterestFilter && titles.length ? { titles } : {}),
		},
		sortBy: [
			{
				key: 'order',
				desc: false,
			},
		],
	};

	const getTransactionsListRequest = useQuery({
		queryKey: ['transactionsList', transactionsRequestParams],
		queryFn: () => getTransactionsList(transactionsRequestParams),
		placeholderData: keepPreviousData,
	});
	const transactions = getTransactionsListRequest.data?.transactions;

	const reorderTransactionMutation = useMutation({
		mutationFn: reorderTransactionAfterOther,

		onMutate: async ({ sourceId, destId, orderDestinationType }) => {
			// take a snapshot the previous value
			const previousTransactions = queryClient.getQueryData([
				'transactionsList',
				transactionsRequestParams,
			]);

			// optimistically update the cache on the fe
			queryClient.setQueryData(
				['transactionsList', transactionsRequestParams],
				(oldData: GetTransactionsRes) => {
					if (!oldData || !oldData.transactions) {
						return oldData;
					}

					// apply same logic for insertion as the BE, optimistically on the FE
					const transactions = [...oldData.transactions];
					const sourceTransaction = transactions.find((tx) => tx.id === sourceId);
					const destIndex = transactions.findIndex((tx) => tx.id === destId);
					if (!sourceTransaction || destIndex === -1) {
						return oldData;
					}
					const updatedTransactions = transactions.filter((tx) => tx.id !== sourceId);
					const shouldMoveBelowDest = orderDestinationType === 'below';

					const sourceTransactionOrder = sourceTransaction.order ?? 0;

					let newOrder;
					if (destIndex < updatedTransactions.length - 1) {
						const nextTransaction = shouldMoveBelowDest
							? updatedTransactions[destIndex + 1]
							: updatedTransactions[destIndex];
						newOrder = (sourceTransactionOrder + (nextTransaction.order ?? 0)) / 2;
					} else if (shouldMoveBelowDest) {
						newOrder = sourceTransactionOrder + 1000;
					} else {
						newOrder = sourceTransactionOrder - 1000;
					}
					sourceTransaction.order = newOrder;
					updatedTransactions.splice(destIndex + 1, 0, sourceTransaction);

					return {
						...oldData,
						transactions: updatedTransactions,
					};
				}
			);

			return { previousTransactions };
		},

		onError: (err, variables, context) => {
			// rollback:
			queryClient.setQueryData(
				['transactionsList', transactionsRequestParams],
				context?.previousTransactions
			);
			showToastV2({ message: 'Failed to reorder transaction', state: 'error' });
		},

		onSuccess: () => {
			showToastV2({ message: 'Transaction reordered successfully', state: 'success' });
		},

		onSettled: () => {
			// Refetch the reorder modal's transactions to get fresh data from backend
			return getTransactionsListRequest.refetch();
		},
	});

	const handleReorder = (
		sourceId: string,
		destId: string,
		isAbove: boolean,
		listRefetch?: () => void
	) => {
		reorderTransactionMutation.mutate(
			{
				sourceId,
				destId,
				orderDestinationType: isAbove ? OrderType.ABOVE : OrderType.BELOW,
			},
			{
				onSuccess: () => {
					listRefetch && listRefetch();
				},
			}
		);
	};

	const columns = useMemo(() => {
		const allColumns = getTransactionsTableColumns(tCapManage, entityCurrency);

		// Filter out the ID and Date columns, as they're the same for all rows
		return allColumns.filter(
			(column) =>
				column.id !== 'id' &&
				column.id !== 'date' &&
				(nomineeStructuresEnabled || column.id !== INTEREST_COLUMN_ID)
		);
	}, [tCapManage, entityCurrency, nomineeStructuresEnabled]);

	const autoReorderMutation = useMutation({
		mutationFn: autoReorderTransactions,

		onMutate: async () => {
			await queryClient.cancelQueries({
				queryKey: ['transactionsList', transactionsRequestParams],
			});
			const previousTransactions = queryClient.getQueryData([
				'transactionsList',
				transactionsRequestParams,
			]);
			return { previousTransactions };
		},

		onError: (err, variables, context) => {
			queryClient.setQueryData(
				['transactionsList', transactionsRequestParams],
				context?.previousTransactions
			);
			showToastV2({ message: 'Failed to auto-reorder transactions', state: 'error' });
		},

		onSuccess: (data) => {
			queryClient.setQueryData(
				['transactionsList', transactionsRequestParams],
				(oldData: GetTransactionsRes) => ({
					...oldData,
					transactions: data,
				})
			);
			showToastV2({ message: 'Transactions auto-reordered successfully', state: 'success' });
		},

		onSettled: () => {
			// Refetch the reorder modal's transactions to get fresh data from backend
			return getTransactionsListRequest.refetch();
		},
	});

	const handleAutoReorder = () => {
		const transactionId = transactions?.[0].id;

		if (!transactionId) {
			return;
		}
		autoReorderMutation.mutate({
			transactionId,
		});
	};

	return {
		transactions,
		isReordering: reorderTransactionMutation.isPending,
		handleReorder,
		columns,
		getTransactionsListRequest,
		handleAutoReorder,
		isAutoReordering: autoReorderMutation.isPending,
		showInterestFilter,
		interestOptions,
		titles,
		onTitlesChange,
	};
};
